import { Link } from 'react-router-dom';
import { BoardroomMode, Brand, EditorLink } from '@aigovops/design-system';
import { roster } from '../data/editors';

/**
 * Public editorial masthead — brand-guide §6.5.1.
 *
 * "Public masthead page. /editors — the full roster, so the credential
 *  story is defensible before a prospect has bought."
 *
 * Every card is a Contract-C12 verified real person. When the roster is
 * small (launch reality: one named editor), the page says so plainly
 * rather than padding with placeholders.
 */
export function Editors() {
  return (
    <BoardroomMode>
      <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100dvh' }}>
        <header style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
          <div
            style={{
              maxWidth: '80rem',
              margin: '0 auto',
              padding: 'var(--space-4) var(--space-6)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
            }}
          >
            <Link
              to="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                textDecoration: 'none',
              }}
            >
              <Brand variant="glyph" />
              <Brand variant="wordmark" />
            </Link>
            <nav
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 'var(--space-4)',
                fontSize: 'var(--font-size-2xs)',
                fontFamily: 'var(--font-ui)',
              }}
            >
              <Link to="/" style={{ color: 'var(--text-muted)' }}>← Back to home</Link>
            </nav>
          </div>
        </header>

        <main style={{ maxWidth: '64rem', margin: '0 auto', padding: 'var(--space-12) var(--space-6)' }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--font-size-3xl)',
              lineHeight: 'var(--line-height-tight)',
              margin: 0,
            }}
          >
            Editorial masthead
          </h1>
          <p
            style={{
              marginTop: 'var(--space-3)',
              maxWidth: '48rem',
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--font-size-md)',
              lineHeight: 'var(--line-height-body)',
            }}
          >
            Every register item, every framework, every citation on gacpro is reviewed and named.
            No black-box scores. No anonymous "our team." The people below sign the work.
          </p>

          <section
            aria-label="Named editors"
            style={{
              marginTop: 'var(--space-10)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))',
              gap: 'var(--space-6)',
            }}
          >
            {roster.map((editor) => (
              <article
                key={editor.id}
                style={{
                  border: '1px solid var(--border)',
                  background: 'var(--surface)',
                  padding: 'var(--space-6)',
                  borderRadius: 'var(--radius-md, 6px)',
                }}
              >
                <h2
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--font-size-xl)',
                    margin: 0,
                  }}
                >
                  <Link
                    to={`/editors/${editor.id}`}
                    style={{ color: 'var(--text)', textDecoration: 'none' }}
                  >
                    {editor.displayName}
                  </Link>
                </h2>
                <p
                  style={{
                    marginTop: 'var(--space-2)',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {editor.credentials.join(' · ')}
                </p>
                <div style={{ marginTop: 'var(--space-4)' }}>
                  <div
                    style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-3xs)',
                      color: 'var(--text-dim)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Domains
                  </div>
                  <div
                    style={{
                      marginTop: 'var(--space-2)',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 'var(--space-2)',
                    }}
                  >
                    {editor.domains.map((d) => (
                      <span
                        key={d}
                        style={{
                          border: '1px solid var(--border)',
                          padding: '2px var(--space-2)',
                          fontFamily: 'var(--font-ui)',
                          fontSize: 'var(--font-size-3xs)',
                          color: 'var(--text-muted)',
                          borderRadius: 'var(--radius-sm, 3px)',
                        }}
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
                <div
                  style={{
                    marginTop: 'var(--space-5)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    fontSize: 'var(--font-size-2xs)',
                    fontFamily: 'var(--font-ui)',
                  }}
                >
                  <Link to={`/editors/${editor.id}`} style={{ color: 'var(--text-muted)' }}>
                    Full card →
                  </Link>
                  <EditorLink editor={editor} />
                </div>
              </article>
            ))}
          </section>

          {roster.length === 1 && (
            <p
              style={{
                marginTop: 'var(--space-8)',
                color: 'var(--text-dim)',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-xs)',
                lineHeight: 'var(--line-height-body)',
                maxWidth: '48rem',
              }}
            >
              At launch the masthead is one named editor. We hire (or contract) additional editors
              as domain coverage grows — the next named lead is FedRAMP, targeted for Q4 2026.
              Editorial capacity is a hiring decision, not a marketing one; adding names before we
              can honor them is the failure mode gacpro exists to avoid.
            </p>
          )}
        </main>

        <footer
          style={{
            borderTop: '1px solid var(--border)',
            marginTop: 'var(--space-16)',
            padding: 'var(--space-8) var(--space-6)',
            background: 'var(--surface)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-dim)',
          }}
        >
          <div style={{ maxWidth: '80rem', margin: '0 auto' }}>
            © 2026 <Brand variant="wordmarkRoman" /> · gacpro.io · hello@gacpro.io
          </div>
        </footer>
      </div>
    </BoardroomMode>
  );
}
